import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Submission, SubmissionList } from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import {
  ActionsCell,
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardHead,
  DefinitionList,
  Drawer,
  Empty,
  Field,
  FilterSelect,
  Note,
  PageHead,
  PersonCell,
  Sect,
  Table,
  Textarea,
  useAdminToast,
} from '../admin-kit';
import { CardLoading, LoadError } from '../opvolging/parts';
import { shortDay, shortMoment } from '../opvolging/format';
import { formatDate } from '../../shared/format';
import { useSession } from '../../shared/session';
import { labelOf } from './field-input';
import { describe } from './submission-row';
import { displayStatus } from './submission-display';

/** Hoeveel antwoorden de samenvatting in de lijst toont: genoeg om te herkennen, niet om te lezen. */
const SUMMARY_FIELDS = 3;

/** De voornaam: "Reden voor Mats", "Mats heeft nog …". */
function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/** De belangrijkste antwoorden op één regel: de eerste drie die ingevuld zijn, zonder foto's. */
function summaryOf(submission: Submission, language: string): string {
  return submission.fields
    .filter((field) => field.type !== 'photo')
    .map((field) => describe(field, submission.data[field.key], language))
    .filter((value) => value !== '—')
    .slice(0, SUMMARY_FIELDS)
    .join(' · ');
}

/**
 * De behandelaarsinbox (V2–V4): alles wat open staat, over alle formulieren heen, oudste eerst.
 *
 * Apart van de bouwer, want het is ander werk en vaak een ander mens: een teamleider keurt verlof
 * goed en bouwt geen formulieren. Een aanvraag opent in een paneel rechts; de lijst blijft erachter
 * en de geopende rij is gemarkeerd.
 *
 * Met `openId` (het eigen adres `/admin/aanvragen/…`, Extra 6 punt 8) staat het paneel van die
 * aanvraag meteen open. Dat adres bestaat zodat een melding over een nieuwe aanvraag ergens naartoe
 * kan springen, en een behandelaar een link kan doorsturen.
 */
export function SubmissionsInboxPage({ openId }: { openId?: string } = {}) {
  const { t, i18n } = useTranslation();
  const { api } = useSession();
  const navigate = useNavigate();
  const toast = useAdminToast();
  const [formId, setFormId] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(openId ?? null);

  const submissions = useQuery({
    queryKey: ['forms', 'inbox'],
    queryFn: async (): Promise<Submission[]> => {
      const { data, error } = await api.GET('/api/v1/forms/submissions', {
        params: { query: { status: 'submitted' } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as SubmissionList).items;
    },
  });

  // De aanvraag van het eigen adres apart ophalen: ze kan al beslist zijn, en staat dan niet meer
  // in de open lijst.
  const linked = useQuery({
    queryKey: ['forms', 'submission', openId],
    enabled: Boolean(openId),
    retry: false,
    queryFn: async (): Promise<Submission> => {
      const { data, error } = await api.GET('/api/v1/forms/submissions/{submissionId}', {
        params: { path: { submissionId: openId as string } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as Submission;
    },
  });

  const open = [...(submissions.data ?? [])].sort((a, b) =>
    a.submittedAt.localeCompare(b.submittedAt),
  );
  const forms = [...new Map(open.map((item) => [item.formId, item.formTitle])).entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const rows = formId ? open.filter((item) => item.formId === formId) : open;
  const selected =
    open.find((item) => item.id === selectedId) ??
    (linked.data && linked.data.id === selectedId ? linked.data : null);

  const close = () => {
    setSelectedId(null);
    // Van het eigen adres terug naar de lijst: anders opent een herlaadbeurt het paneel opnieuw.
    if (openId) void navigate({ to: '/admin/aanvragen' });
  };

  return (
    <AdminPage>
      <PageHead title={t('formsInbox.title')} sub={t('beheer.opvolging.requests.sub')} />

      {openId && linked.isError ? (
        <Note
          tone="danger"
          role="alert"
          lead={t('formsInbox.notFound')}
          testId="submission-missing"
        >
          {t('formsInbox.notFoundHint')}
        </Note>
      ) : null}

      {submissions.isPending ? (
        <Card>
          <CardLoading rows={4} />
        </Card>
      ) : submissions.isError ? (
        <Card>
          <LoadError onRetry={() => void submissions.refetch()} error={submissions.error} />
        </Card>
      ) : open.length === 0 ? (
        <Card>
          <Empty
            icon={<AdminIcon name="inbox" size={26} />}
            title={t('formsInbox.emptyTitle')}
            text={t('beheer.opvolging.requests.emptyText')}
            testId="inbox-empty"
          />
        </Card>
      ) : (
        <Card>
          <CardHead
            title={t('beheer.opvolging.requests.open')}
            count={open.length}
            countAttention
            actions={
              <FilterSelect
                label={t('beheer.opvolging.requests.form')}
                allLabel={t('beheer.opvolging.requests.allForms')}
                options={forms}
                value={formId}
                onChange={setFormId}
                testId="inbox-form-filter"
              />
            }
          />
          <Table label={t('beheer.opvolging.requests.open')}>
            <thead>
              <tr>
                <th>{t('beheer.opvolging.requests.colPerson')}</th>
                <th>{t('beheer.opvolging.requests.colForm')}</th>
                <th>{t('beheer.opvolging.requests.colSubmitted')}</th>
                <th>{t('beheer.opvolging.requests.colSummary')}</th>
                <th className="act">
                  <span className="a-sr">{t('beheer.opvolging.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((submission) => (
                <tr
                  key={submission.id}
                  data-testid="submission-row"
                  className={submission.id === selectedId ? 'sel' : undefined}
                >
                  <td>
                    <PersonCell name={submission.personName} />
                  </td>
                  <td>{submission.formTitle}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{shortMoment(submission.submittedAt)}</td>
                  <td>
                    <span className="a-muted">{summaryOf(submission, i18n.language)}</span>
                  </td>
                  <ActionsCell>
                    <Btn
                      variant="ghost"
                      size="sm"
                      testId="submission-open"
                      onClick={() => setSelectedId(submission.id)}
                    >
                      {t('formsInbox.open')}
                    </Btn>
                  </ActionsCell>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {selected ? (
        <SubmissionDrawer
          key={selected.id}
          submission={selected}
          others={open.filter(
            (item) => item.personId === selected.personId && item.id !== selected.id,
          )}
          onClose={close}
          onDecided={(message) => {
            toast.show(message);
            close();
          }}
        />
      ) : null}
      {toast.element}
    </AdminPage>
  );
}

/**
 * Eén aanvraag op haar eigen adres: dezelfde inbox, met haar paneel open (V3 "een eigen adres mag
 * blijven en opent dan hetzelfde paneel").
 */
export function SubmissionPage({ submissionId }: { submissionId: string }) {
  return <SubmissionsInboxPage key={submissionId} openId={submissionId} />;
}

/**
 * Het paneel van één aanvraag (V3): de antwoorden, de aanvrager, wat die nog meer lopen heeft, en
 * de beslissing. De reden is verplicht bij afwijzen: "afgekeurd" zonder reden laat een medewerker
 * met een vraag achter die niemand meer beantwoordt.
 */
function SubmissionDrawer({
  submission,
  others,
  onClose,
  onDecided,
}: {
  submission: Submission;
  others: Submission[];
  onClose: () => void;
  onDecided: (message: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const name = firstName(submission.personName);

  const decide = useMutation({
    mutationFn: async (decision: 'approve' | 'reject') => {
      const { error } = await api.POST('/api/v1/forms/submissions/{submissionId}/decide', {
        params: { path: { submissionId: submission.id } },
        body: { decision, note },
      });
      if (error) throw error;
    },
    onSuccess: (_result, decision) => {
      void queryClient.invalidateQueries({ queryKey: ['forms'] });
      onDecided(decision === 'approve' ? t('formsAdmin.approved') : t('formsAdmin.rejected'));
    },
    onError: (error) => setFailure(problemDetail(error) ?? t('errors.generic')),
  });

  const submit = (decision: 'approve' | 'reject') => {
    setFailure(null);
    if (decision === 'reject' && !note.trim()) {
      setNoteError(t('beheer.opvolging.requests.reasonRequired', { name }));
      return;
    }
    setNoteError(null);
    decide.mutate(decision);
  };

  return (
    <Drawer
      open
      onClose={onClose}
      testId="submission-drawer"
      title={t('beheer.opvolging.requests.drawerTitle', {
        form: submission.formTitle,
        name: submission.personName,
      })}
      sub={t('beheer.opvolging.requests.drawerSub', {
        when: shortMoment(submission.submittedAt),
        version: submission.versionNumber,
      })}
      footer={
        submission.canDecide ? (
          <>
            <Btn
              variant="danger-sec"
              icon={<AdminIcon name="x" />}
              testId="submission-reject"
              disabled={decide.isPending}
              onClick={() => submit('reject')}
            >
              {t('formsAdmin.reject')}
            </Btn>
            <Btn
              icon={<AdminIcon name="check" />}
              testId="submission-approve"
              disabled={decide.isPending}
              aria-busy={decide.isPending && decide.variables === 'approve'}
              onClick={() => submit('approve')}
            >
              {t('formsAdmin.approve')}
            </Btn>
          </>
        ) : (
          <Btn variant="sec" onClick={onClose}>
            {t('beheer.kit.close')}
          </Btn>
        )
      }
    >
      {displayStatus(submission) !== 'submitted' ? (
        <div className="a-actions">
          <Badge
            tone={
              submission.status === 'approved'
                ? 'ok'
                : submission.status === 'rejected'
                  ? 'danger'
                  : 'neutral'
            }
          >
            {t(`forms.status.${displayStatus(submission)}`)}
          </Badge>
          {submission.decidedAt ? (
            <span className="a-meta">{formatDate(submission.decidedAt)}</span>
          ) : null}
        </div>
      ) : null}

      {/* De waarden met de labels van de versie waartegen is ingediend — niet van de nieuwste. */}
      <DefinitionList
        items={submission.fields.map((field) => {
          const value = describe(field, submission.data[field.key], i18n.language);
          return [
            labelOf(field.label, i18n.language, field.key),
            value === '—' ? <span className="a-muted">—</span> : value,
          ];
        })}
      />

      <Sect title={t('beheer.opvolging.requests.applicant')}>
        <PersonCell name={submission.personName} meta={submission.employeeNumber ?? undefined} />
      </Sect>

      {others.length > 0 ? (
        <Note>
          {t('beheer.opvolging.requests.others', {
            name,
            count: others.length,
            list: others
              .map((other) => `${other.formTitle} (${shortDay(other.submittedAt)})`)
              .join(', '),
          })}
        </Note>
      ) : null}

      {submission.decisionNote ? (
        <Note tone="info">
          {t('formsAdmin.decisionBy', {
            name: submission.decidedByName ?? '?',
            note: submission.decisionNote,
          })}
        </Note>
      ) : null}

      {submission.canDecide ? (
        <Field label={t('beheer.opvolging.requests.reason', { name })} error={noteError}>
          {(props) => (
            <Textarea
              {...props}
              value={note}
              maxLength={500}
              placeholder={t('beheer.opvolging.requests.reasonPlaceholder')}
              data-testid="submission-note"
              onChange={(event) => {
                setNote(event.target.value);
                if (noteError && event.target.value.trim()) setNoteError(null);
              }}
            />
          )}
        </Field>
      ) : null}

      {failure ? (
        <Note tone="danger" role="alert">
          {failure}
        </Note>
      ) : null}
    </Drawer>
  );
}
